import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../contexts/ThemeContext";
import { useView } from "../contexts/ViewContext";
import googleAdsIconDark from "../assets/icons/google-ads-dark.svg";
import googleAdsIconLight from "../assets/icons/google-ads-light.svg";
import ThinScroll from "./shared/ThinScroll";
import { canAccessHub, CLIENT_HUB_NAV_VISIBLE } from "../pages/hub/access";

/* ───────────── ICONS ───────────── */
const Icon = ({ children, ...props }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    {children}
  </svg>
);

const HomeIcon = () => <Icon><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></Icon>;
// Portal "MY WORK" — a clipboard/checklist mark for the work-in-flight surface.
const WorkIcon = () => <Icon><path d="M9 2h6a1 1 0 0 1 1 1v1h2a1 1 0 0 1 1 1v15a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h2V3a1 1 0 0 1 1-1z"/><path d="M9 14l2 2 4-4"/></Icon>;
const GuestsIcon = () => <Icon><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></Icon>;
const RevenueIcon = () => <Icon><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></Icon>;
const MarketingIcon = () => <Icon><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></Icon>;
const PropertyIcon = () => <Icon><path d="M3 9l2-2m0 0l7-7 7 7M5 7v13a1 1 0 001 1h3m10-14l2 2m-2-2v13a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/></Icon>;
const MarketIcon = () => <Icon><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></Icon>;
const AgentsIcon = () => <Icon><rect x="3" y="11" width="18" height="10" rx="2"/><circle cx="12" cy="5" r="2"/><path d="M12 7v4"/><line x1="8" y1="16" x2="8" y2="16"/><line x1="16" y1="16" x2="16" y2="16"/></Icon>;

const ChatIcon = () => <Icon><path d="M21 15a4 4 0 0 1-4 4H7l-4 4V5a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z"/></Icon>;
const VoiceIcon = () => <Icon><rect x="9" y="2" width="6" height="12" rx="3"/><path d="M5 10a7 7 0 0 0 14 0"/><path d="M12 19v4"/></Icon>;
const BookingIcon = () => <Icon><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></Icon>;
const VisitorsIcon = () => <Icon><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></Icon>;
const AnalyticsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <rect x="3" y="4" width="5" height="17" rx="2.5" fill="currentColor"/>
    <rect x="10" y="9" width="5" height="12" rx="2.5" fill="currentColor"/>
    <rect x="17" y="14" width="5" height="7" rx="2.5" fill="currentColor"/>
  </svg>
);
const AimIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M13 2.5l1.9 5.6 5.6 1.9-5.6 1.9-1.9 5.6-1.9-5.6L5.5 10l5.6-1.9L13 2.5z"/>
    <path d="M19 14.5l.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8.8-2.2z"/>
  </svg>
);
const EventsIcon = () => <Icon><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/><path d="M12 13l1.2 2.6 2.8.4-2 2 .5 2.8L12 19.5 9.5 20.8l.5-2.8-2-2 2.8-.4z"/></Icon>;
const RouteIcon = () => <Icon><circle cx="5" cy="6" r="2"/><circle cx="19" cy="18" r="2"/><path d="M5 8c0 7 14 1 14 8"/></Icon>;
const CompetitiveIcon = () => <Icon><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></Icon>;
const SearchIcon = () => <Icon><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></Icon>;
const SeoIcon = () => <Icon><path d="M21 11V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h6"/><path d="M14 14l3 3 6-6"/></Icon>;
const SocialIcon = () => <Icon><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></Icon>;
const ChannelIcon = () => <Icon><circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8"/></Icon>;
const PhoneIcon = () => <Icon><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/></Icon>;
const HeartIcon = () => <Icon><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></Icon>;
const TrendUpIcon = () => <Icon><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></Icon>;
const ScaleIcon = () => <Icon><path d="M12 3v18"/><path d="M5 7h14"/><path d="M5 7l-2 6a4 4 0 0 0 8 0l-2-6"/><path d="M19 7l-2 6a4 4 0 0 0 8 0l-2-6"/></Icon>;
const StarIcon = () => <Icon><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></Icon>;
const PinIcon = () => <Icon><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></Icon>;
const CameraIcon = () => <Icon><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></Icon>;
const GlobeIcon = () => <Icon><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></Icon>;
const CloudIcon = () => <Icon><path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/></Icon>;
const AwardIcon = () => <Icon><circle cx="12" cy="8" r="7"/><polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"/></Icon>;
// Organizations / Users icons for the ClientHub menu system. Mirror the in-hub
// HubSidebar's OrgIcon / UserIcon exactly (same SVG paths) so the two sidebars
// read as one surface.
const OrgIcon = () => <Icon><path d="M3 21h18"/><path d="M5 21V7l8-4v18"/><path d="M19 21V11l-6-4"/><path d="M9 9v.01M9 12v.01M9 15v.01M9 18v.01"/></Icon>;
const UserIcon = () => <Icon><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></Icon>;
export const ChevronDownIcon = ({ className = "" }) => (
  <svg className={className} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);

const GoogleAdsIcon = ({ isDarkMode, dimmed }) => (
  <img
    src={isDarkMode ? googleAdsIconLight : googleAdsIconDark}
    alt=""
    width={20}
    height={20}
    className={dimmed ? "opacity-40 grayscale" : ""}
  />
);

/**
 * Which sidebar row should read as active on the conversation master-detail page.
 *
 * The analysis band now lives under MY AGENTS → Chat and → Voice, which each
 * route with `view=intelligence`. So chat highlights the My Agents "chat" row and
 * voice the "voice" row — the row that actually carries the surface you are on.
 *
 * My Guests no longer holds a live intelligence row: Chat Intelligence was
 * removed (its band moved to My Agents → Chat), and Call Intelligence is a
 * disabled "soon" teaser, not a live destination — so neither is an active target.
 *
 * Any other modality returns null, preserving the previous behaviour of
 * highlighting nothing.
 *
 * IT ALSO RETURNS NULL FOR A MODALITY THE PROPERTY IS NOT ENTITLED TO, and that
 * is not a detail. `isDisabled` is computed as `!item.enabled && !isActive`, so
 * marking a row active UN-DIMS it. A dimmed row is a statement that this property
 * does not have the feature; un-dimming it because somebody opened a deep link
 * contradicts that statement. Once `dashboard.chat` / `dashboard.voice` are
 * registered it would get worse — a flag deliberately set to OFF would still
 * render its row live and highlighted to anyone holding the URL, which reads as
 * an entitlement leak even though nothing is actually unlocked.
 *
 * The cost of failing back to null is that an unentitled deep link highlights
 * nothing, which is exactly today's behaviour. Asserting nothing beats asserting
 * something false.
 *
 * The entitlement flags are ARGUMENTS rather than context reads, so this stays a
 * pure function next to the config whose keys it returns.
 *
 * @param {string|null} modality
 * @param {{ showChat?: boolean, showVoice?: boolean }} [entitlements] omitted
 *   entirely → treated as entitled, so a caller that has no flags to hand keeps
 *   the previous behaviour rather than silently highlighting nothing.
 */
export const navActiveKeyForModality = (modality, entitlements) => {
  const allowed = (flag) => entitlements === undefined || !!flag;
  if (modality === 'chat') return allowed(entitlements?.showChat) ? 'chat' : null;
  // Voice highlights the My Agents -> Voice row, which now carries the analysis
  // band. Call Intelligence stays a "soon" teaser under My Guests and is not a
  // live surface, so it is not the target. Gated on showVoice, as chat is on showChat.
  if (modality === 'voice') return allowed(entitlements?.showVoice) ? 'voice' : null;
  return null;
};

/* ═══════════════════ SIDEBAR CONFIG (single source of truth) ═══════════════════ */
// chatRoute / voiceRoute default to /dashboard for admins and to the client's
// own transcript list (filtered by modality) when we know the selected project.
export const buildGroups = ({ showChat, showVoice, showBookings, showCdp, showCompetitive, showGoogleAds, showAnalytics, showAim, showEvents, showRoute, showWeatherIntelligence, showSearchConsole, showHubClients, isClient, isDarkMode, chatRoute, voiceRoute, chatIntelligenceRoute, voiceIntelligenceRoute, showPortal, showLocalIntelligence }) => [
  {
    key: "home",
    label: "Home",
    icon: <HomeIcon />,
    items: [
      { key: "home", label: "Dashboard", route: isClient ? "/client-dashboard" : "/dashboard", enabled: true, status: "now", icon: <HomeIcon /> },
      // Portal "MY WORK" (Epic 11 · S11.3) — a home-level surface for clients +
      // operators, gated on the `portal` feature flag. Routed at /my-work.
      { key: "my-work", label: "MY WORK", route: "/my-work", enabled: !!showPortal, status: "now", icon: <WorkIcon /> },
    ],
  },
  // ClientHub — internal account-manager surface. The whole group is omitted for
  // clients (showHubClients is false for role 3) rather than shown-disabled, so
  // the surface's existence isn't leaked to a customer.
  //
  // Organizations · Clients · Users are the agency-running trio that used to live
  // in the dashboard top nav. They now live here as the ClientHub menu system,
  // mirroring the in-hub HubSidebar "Admin" section. Clients keeps its own hub
  // surface (/hub/clients); Organizations/Users route through the /hub/admin/*
  // aliases that redirect to the platform-admin pages (see App.js §Admin routes).
  ...(showHubClients ? [{
    key: "client-hub",
    label: "Client Hub",
    icon: <GuestsIcon />,
    items: [
      { key: "hub-organizations", label: "Organizations", route: "/hub/admin/organizations", enabled: true, status: "now", icon: <OrgIcon /> },
      { key: "hub-clients", label: "Clients", route: "/hub/clients", enabled: true, status: "now", icon: <GuestsIcon /> },
      { key: "hub-users", label: "Users", route: "/hub/admin/users", enabled: true, status: "now", icon: <UserIcon /> },
    ],
  }] : []),
  {
    key: "my-guests",
    label: "My Guests",
    icon: <GuestsIcon />,
    items: [
      { key: "visitors", label: "Visitor Intelligence", route: "/visitors", enabled: showCdp, status: "now", icon: <VisitorsIcon /> },
      { key: "booking", label: "Booking Intelligence", route: "/bookings", enabled: showBookings, status: "now", icon: <BookingIcon /> },
      // Call Intelligence is a "soon" placeholder, not a live page: no route, no
      // enabled flag, a Soon badge like Revenue/Market Intelligence. The voice
      // analysis band still exists and is reachable by direct URL — this hides it
      // from the menu, per the product call, without gating the page itself.
      { key: "call-intel", label: "Call Intelligence", status: "soon", icon: <PhoneIcon /> },
      { key: "guest-intel", label: "Guest Intelligence", status: "later", icon: <HeartIcon /> },
    ],
  },
  {
    key: "my-revenue",
    label: "My Revenue",
    icon: <RevenueIcon />,
    items: [
      { key: "competitive", label: "Competitive Intelligence", route: "/competitive-intelligence", enabled: showCompetitive, status: "now", icon: <CompetitiveIcon /> },
      { key: "revenue-intel", label: "Revenue Intelligence", status: "soon", icon: <TrendUpIcon /> },
      { key: "demand-intel", label: "Demand Intelligence", status: "soon", icon: <TrendUpIcon /> },
      { key: "rate-parity", label: "Rate Parity", status: "later", icon: <ScaleIcon /> },
    ],
  },
  {
    key: "my-marketing",
    label: "My Marketing",
    icon: <MarketingIcon />,
    items: [
      { key: "google-ads", label: "Google Ads", route: "/google-ads", enabled: showGoogleAds, status: "now", icon: <GoogleAdsIcon isDarkMode={isDarkMode} dimmed={!showGoogleAds} /> },
      { key: "analytics", label: "Website Analytics", route: "/google-analytics", enabled: showAnalytics, status: "now", icon: <AnalyticsIcon /> },
      { key: "aim", label: "AI Visibility", route: "/ai-visibility", enabled: showAim, status: "now", icon: <AimIcon /> },
      { key: "seo-intel", label: "SEO Intelligence", status: "soon", icon: <SeoIcon /> },
      { key: "social-intel", label: "Social Intelligence", status: "later", icon: <SocialIcon /> },
      { key: "channel-intel", label: "Channel Intelligence", status: "later", icon: <ChannelIcon /> },
    ],
  },
  {
    key: "my-property",
    label: "My Property",
    icon: <PropertyIcon />,
    items: [
      // 2026-08-13 — promoted from a "soon" placeholder in My Marketing. The spec puts
      // Search Console under My Property: it reports on how the park's own site is
      // found and indexed, not on a marketing channel it buys.
      { key: "search-console", label: "Search Console", route: "/search-console", enabled: showSearchConsole, status: "now", icon: <SearchIcon /> },
      { key: "reputation", label: "Reputation Intelligence", status: "soon", icon: <StarIcon /> },
      { key: "gbp-intel", label: "GBP Intelligence", status: "soon", icon: <PinIcon /> },
      { key: "photo-intel", label: "Photo Intelligence", status: "later", icon: <CameraIcon /> },
      { key: "website-intel", label: "Website Intelligence", status: "later", icon: <GlobeIcon /> },
      { key: "weather-intel", label: "Weather Intelligence", route: "/weather-intelligence", enabled: showWeatherIntelligence, status: "now", icon: <CloudIcon /> },
      { key: "local-intel", label: "Around the Park", route: "/local-intelligence", enabled: showLocalIntelligence, status: "now", icon: <PinIcon /> },
      { key: "benchmark", label: "Benchmark Intelligence", status: "later", icon: <AwardIcon /> },
    ],
  },
  {
    key: "my-market",
    label: "My Market",
    icon: <MarketIcon />,
    items: [
      { key: "events", label: "Events Intelligence", route: "/event-intelligence", enabled: showEvents, status: "now", icon: <EventsIcon /> },
      { key: "route", label: "Route Intelligence", route: "/route-intelligence", enabled: showRoute, status: "now", icon: <RouteIcon /> },
      { key: "market-intel", label: "Market Intelligence", status: "soon", icon: <GlobeIcon /> },
    ],
  },
  {
    key: "my-agents",
    label: "My Agents",
    icon: <AgentsIcon />,
    items: [
      // My Agents -> Chat carries the Chat Intelligence analysis band via
      // `view=intelligence`. The old My Guests -> Chat Intelligence row is gone;
      // this is its home now. (Voice is the same; Call Intelligence stays a Soon teaser.)
      { key: "chat", label: "Chat", route: chatRoute || (isClient ? "/client-dashboard" : "/dashboard"), enabled: showChat, status: "now", icon: <ChatIcon /> },
      // My Agents -> Voice carries the Voice Intelligence analysis band via
      // `view=intelligence`. (My Guests -> Call Intelligence is a "soon" teaser,
      // not a live surface.) Chat is unchanged: its band stays under My Guests.
      { key: "voice", label: "Phone", route: voiceRoute || (isClient ? "/client-dashboard" : "/dashboard"), enabled: showVoice, status: "now", icon: <VoiceIcon /> },
    ],
  },
];

/* ═══════════════════ STATUS BADGE ═══════════════════ */
export const StatusBadge = ({ status }) => {
  if (status === "soon") return <span className="ml-auto text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300">Soon</span>;
  if (status === "later") return <span className="ml-auto text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-500">Later</span>;
  return null;
};

/* ═══════════════════ MAIN COMPONENT ═══════════════════ */
const STORAGE_KEY = "navsidebar.expandedGroups";

export default function NavSidebarGrouped({ activePage }) {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const view = useView();

  const role = view.role;
  const isClient = view.isClient;

  // Chat / Voice route to the transcript list scoped by modality
  // (?modality=chat|voice). Client + admin paths use different list-page
  // components but both accept the same query param.
  //   admin  → /client/:projectId  (ProjectDetailPage)
  //   client → /client-projects/:projectId  (ClientProjectDetailPage)
  const selectedProjectId = view.selectedProjectId;
  const listRouteFor = (modality) => {
    if (!selectedProjectId) return isClient ? "/client-dashboard" : "/dashboard";
    const base = isClient
      ? `/client-projects/${selectedProjectId}`
      : `/client/${selectedProjectId}`;
    return `${base}?modality=${modality}`;
  };
  // Base modality URLs. As of V6 these ARE the My Agents → Phone / Chat routes:
  // modality alone renders the ConversationPage. The ?view=intelligence marker is
  // retired — the analysis IS the page now, not a toggle over a transcript list.
  const chatRoute = listRouteFor("chat");
  const voiceRoute = listRouteFor("voice");

  // Map ViewContext flags
  const groups = useMemo(() => buildGroups({
    showChat: !!view.showChat,
    showVoice: !!view.showVoice,
    showBookings: !!view.showBookings,
    showCdp: !!view.showCdp,
    showCompetitive: !!view.showCompetitiveIntelligence,
    showGoogleAds: !!view.showGoogleAds,
    showAnalytics: !!view.showGoogleAnalytics,
    showAim: !!view.showAim,
    showEvents: !!view.showEventIntelligence,
    showRoute: !!view.showRouteIntelligence,
    // Already mirrors the route's gate — see `showSearchConsole` in ViewContext.
    showSearchConsole: !!view.showSearchConsole,
    // Portal "MY WORK" (Epic 11 · S11.3) — gated on the `portal` feature flag,
    // mirroring the FeatureRoute gate in App.js so the nav row + route agree.
    showPortal: !!view.showPortal,
    // 2026-08-13 — Weather Intelligence. Was missing from the buildGroups call,
    // so the nav entry always got undefined (falsy) even when the flag was on.
    showWeatherIntelligence: !!view.showWeatherIntelligence,
    showLocalIntelligence: !!view.showLocalIntelligence,
    // ClientHub visibility mirrors the page-level gate exactly (canAccessHub) so
    // the nav row and the route can never disagree about who may enter — but the
    // feature is currently hidden from nav for everyone (CLIENT_HUB_NAV_VISIBLE).
    // Super Admin reaches it via the Profile page instead.
    showHubClients: CLIENT_HUB_NAV_VISIBLE && canAccessHub(view),
    isClient,
    isDarkMode,
    chatRoute,
    voiceRoute,
  }), [view, isClient, isDarkMode, chatRoute, voiceRoute]);

  // Find which group contains the active item
  const activeGroupKey = useMemo(() => {
    for (const g of groups) {
      if (g.items.some(i => i.key === activePage)) return g.key;
    }
    return "home";
  }, [groups, activePage]);

  // Expanded state — persists per-browser
  const [expanded, setExpanded] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) return JSON.parse(stored);
    } catch {}
    return { [activeGroupKey]: true };
  });

  // Auto-expand the group containing the active page when it changes
  useEffect(() => {
    setExpanded(prev => prev[activeGroupKey] ? prev : { ...prev, [activeGroupKey]: true });
  }, [activeGroupKey]);

  // Persist expansion state
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(expanded)); } catch {}
  }, [expanded]);

  const toggleGroup = (key) => {
    setExpanded(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <>
      <ThinScroll
        as="aside"
        className="hidden md:flex flex-col w-56 border-r dark:border-gray-800 bg-white dark:bg-gray-900 fixed left-0 overflow-y-auto z-30"
        style={{
          top: 'var(--app-header-h, 72px)',
          height: 'calc(100vh - var(--app-header-h, 72px))',
        }}
      >
      <nav className="px-2 pt-3 pb-6 space-y-0.5">
        {groups.map((group) => {
          const isExpanded = !!expanded[group.key];
          const isActiveGroup = group.key === activeGroupKey;
          const groupHasActive = group.items.some(i => i.key === activePage);

          return (
            <div key={group.key}>
              {/* Group header */}
              <button
                type="button"
                onClick={() => toggleGroup(group.key)}
                className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg transition-colors text-left ${
                  groupHasActive
                    ? "text-[#32B1FF] font-semibold"
                    : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                }`}
              >
                <span className={groupHasActive ? "text-[#32B1FF]" : "text-gray-500 dark:text-gray-400"}>
                  {group.icon}
                </span>
                <span className="text-xs font-semibold uppercase tracking-wide flex-1">{group.label}</span>
                <ChevronDownIcon className={`text-gray-400 transition-transform ${isExpanded ? "" : "-rotate-90"}`} />
              </button>

              {/* Group items */}
              {isExpanded && (
                <div className="mt-0.5 mb-1 space-y-0.5">
                  {group.items.map((item) => {
                    const isActive = activePage === item.key;
                    const isSoonOrLater = item.status === "soon" || item.status === "later";
                    const isDisabled = !isSoonOrLater && !item.enabled && !isActive;
                    const isClickable = item.status === "now" && (item.enabled || isActive) && !isActive;

                    return (
                      <button
                        key={item.key}
                        type="button"
                        disabled={!isClickable && !isActive}
                        onClick={() => isClickable && item.route && navigate(item.route)}
                        title={
                          item.status === "soon" ? `${item.label} — Coming Soon` :
                          item.status === "later" ? `${item.label} — Planned` :
                          isDisabled ? `${item.label} — Not enabled for this project` :
                          item.label
                        }
                        className={`w-full flex items-center gap-2 pl-8 pr-2.5 py-1.5 rounded-md text-xs transition-colors ${
                          isActive
                            ? "bg-[#32B1FF]/10 text-[#32B1FF] font-semibold"
                            : isSoonOrLater
                            ? "text-gray-500 dark:text-gray-500 cursor-default"
                            : isDisabled
                            ? "text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-70"
                            : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                        }`}
                      >
                        <span className={`shrink-0 ${
                          isActive ? "text-[#32B1FF]" :
                          isSoonOrLater ? "text-gray-400 dark:text-gray-600" :
                          isDisabled ? "text-gray-300 dark:text-gray-700" :
                          "text-gray-500 dark:text-gray-400"
                        }`}>
                          {item.icon}
                        </span>
                        <span className="flex-1 text-left truncate">{item.label}</span>
                        <StatusBadge status={item.status} />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </ThinScroll>
    {/* Reserves layout space for the fixed sidebar so the flex row still aligns. */}
    <div className="hidden md:block w-56 shrink-0" aria-hidden="true" />
    </>
  );
}
